import { BaseClient } from '../BaseClient';
import type {
  BatchCreateMembershipRequest,
  Membership,
  MembershipChannel,
  MembershipChannelList,
  MembershipDashboard,
  MembershipDashboardList,
  MembershipDashboardRow,
  MembershipDashboardRowList,
  MembershipInstanceLine,
  MembershipInstanceLineList,
  MembershipLine,
  MembershipLineList,
  MembershipList,
  MembershipStatus,
  MembershipStatusList,
  MembershipType,
  MembershipTypeLine,
  MembershipTypeLineList,
  MembershipTypeList,
  RenewalReminderPreviewReq,
} from '../compiled-protos/membership';
import { MembershipServiceClient } from '../compiled-protos/membership.client';

/**
 * MembershipClient wraps MembershipServiceClient for the residential and commercial
 * membership module. Memberships belong to a property under a customer contract;
 * MembershipType is the plan catalog. MembershipInstanceLine holds the per-membership
 * snapshot of plan line items taken at membership create time.
 */
export class MembershipClient extends BaseClient {
  private svc: MembershipServiceClient;

  constructor(host: string, userID?: number) {
    super(host, userID);
    this.svc = new MembershipServiceClient(this.transport);
  }

  public async Create(req: Membership): Promise<Membership | undefined> {
    try {
      return (await this.svc.create(req, this.getMetaData()).response);
    } catch (err) {
      console.error('Failed to create membership:', err);
      return undefined;
    }
  }

  public async Get(req: Membership): Promise<Membership | undefined> {
    try {
      return (await this.svc.get(req, this.getMetaData()).response);
    } catch (err) {
      console.error('Failed to get membership:', err);
      return undefined;
    }
  }

  public async BatchGet(req: Membership): Promise<MembershipList | undefined> {
    try {
      return (await this.svc.batchGet(req, this.getMetaData()).response);
    } catch (err) {
      console.error('Failed to batch get memberships:', err);
      return undefined;
    }
  }

  public async Update(req: Membership): Promise<Membership | undefined> {
    try {
      return (await this.svc.update(req, this.getMetaData()).response);
    } catch (err) {
      console.error('Failed to update membership:', err);
      return undefined;
    }
  }

  // Errors propagate: the server names why a reminder was refused.
  public async SendRenewalReminderPreview(req: RenewalReminderPreviewReq): Promise<boolean> {
    const res = await this.svc.sendRenewalReminderPreview(req, this.getMetaData()).response;
    return res.value;
  }

  public async Delete(req: Membership): Promise<void> {
    try {
      await this.svc.delete(req, this.getMetaData()).response;
    } catch (err) {
      console.error('Failed to delete membership:', err);
    }
  }

  public async CreateMembershipType(req: MembershipType): Promise<MembershipType | undefined> {
    try {
      return (await this.svc.createMembershipType(req, this.getMetaData()).response);
    } catch (err) {
      console.error('Failed to create membership type:', err);
      return undefined;
    }
  }

  public async GetMembershipType(req: MembershipType): Promise<MembershipType | undefined> {
    try {
      return (await this.svc.getMembershipType(req, this.getMetaData()).response);
    } catch (err) {
      console.error('Failed to get membership type:', err);
      return undefined;
    }
  }

  public async BatchGetMembershipType(req: MembershipType): Promise<MembershipTypeList | undefined> {
    try {
      return (await this.svc.batchGetMembershipType(req, this.getMetaData()).response);
    } catch (err) {
      console.error('Failed to batch get membership types:', err);
      return undefined;
    }
  }

  public async UpdateMembershipType(req: MembershipType): Promise<MembershipType | undefined> {
    try {
      return (await this.svc.updateMembershipType(req, this.getMetaData()).response);
    } catch (err) {
      console.error('Failed to update membership type:', err);
      return undefined;
    }
  }

  public async DeleteMembershipType(req: MembershipType): Promise<boolean> {
    try {
      await this.svc.deleteMembershipType(req, this.getMetaData()).response;
      return true;
    } catch (err) {
      console.error('Failed to delete membership type:', err);
      return false;
    }
  }

  public async BatchGetMembershipLine(req: MembershipLine): Promise<MembershipLineList | undefined> {
    try {
      return (await this.svc.batchGetMembershipLine(req, this.getMetaData()).response);
    } catch (err) {
      console.error('Failed to batch get membership lines:', err);
      return undefined;
    }
  }

  public async CreateMembershipLine(req: MembershipLine): Promise<MembershipLine | undefined> {
    try {
      return (await this.svc.createMembershipLine(req, this.getMetaData()).response);
    } catch (err) {
      console.error('Failed to create membership line:', err);
      return undefined;
    }
  }

  public async UpdateMembershipLine(req: MembershipLine): Promise<MembershipLine | undefined> {
    try {
      return (await this.svc.updateMembershipLine(req, this.getMetaData()).response);
    } catch (err) {
      console.error('Failed to update membership line:', err);
      return undefined;
    }
  }

  public async BatchGetMembershipTypeLine(req: MembershipTypeLine): Promise<MembershipTypeLineList | undefined> {
    try {
      return (await this.svc.batchGetMembershipTypeLine(req, this.getMetaData()).response);
    } catch (err) {
      console.error('Failed to batch get membership type lines:', err);
      return undefined;
    }
  }

  public async CreateMembershipTypeLine(req: MembershipTypeLine): Promise<MembershipTypeLine | undefined> {
    try {
      return (await this.svc.createMembershipTypeLine(req, this.getMetaData()).response);
    } catch (err) {
      console.error('Failed to create membership type line:', err);
      return undefined;
    }
  }

  public async UpdateMembershipTypeLine(req: MembershipTypeLine): Promise<MembershipTypeLine | undefined> {
    try {
      return (await this.svc.updateMembershipTypeLine(req, this.getMetaData()).response);
    } catch (err) {
      console.error('Failed to update membership type line:', err);
      return undefined;
    }
  }

  public async DeleteMembershipTypeLine(req: MembershipTypeLine): Promise<void> {
    try {
      await this.svc.deleteMembershipTypeLine(req, this.getMetaData()).response;
    } catch (err) {
      console.error('Failed to delete membership type line:', err);
    }
  }

  public async createMembershipInstanceLine(
    req: MembershipInstanceLine
  ): Promise<MembershipInstanceLine | undefined> {
    try {
      return (await this.svc.createMembershipInstanceLine(req, this.getMetaData()).response);
    } catch (err) {
      console.error('Failed to create membership instance line:', err);
      return undefined;
    }
  }

  public async BatchGetMembershipInstanceLine(
    req: MembershipInstanceLine
  ): Promise<MembershipInstanceLineList | undefined> {
    try {
      return (await this.svc.batchGetMembershipInstanceLine(req, this.getMetaData()).response);
    } catch (err) {
      console.error('Failed to batch get membership instance lines:', err);
      return undefined;
    }
  }

  public async BatchGetMembershipChannel(
    req: MembershipChannel
  ): Promise<MembershipChannelList | undefined> {
    try {
      return (await this.svc.batchGetMembershipChannel(req, this.getMetaData()).response);
    } catch (err) {
      console.error('Failed to batch get membership channels:', err);
      return undefined;
    }
  }

  public async BatchGetMembershipStatus(
    req: MembershipStatus
  ): Promise<MembershipStatusList | undefined> {
    try {
      return (await this.svc.batchGetMembershipStatus(req, this.getMetaData()).response);
    } catch (err) {
      console.error('Failed to batch get membership statuses:', err);
      return undefined;
    }
  }

  public async BatchCreateMembership(
    req: BatchCreateMembershipRequest
  ): Promise<MembershipList | undefined> {
    try {
      return (await this.svc.batchCreateMembership(req, this.getMetaData()).response);
    } catch (err) {
      console.error('Failed to batch create memberships:', err);
      return undefined;
    }
  }

  /* MEMBERSHIPS DASHBOARD (paginated contract grain + membership sub-rows) */
  // No catch on either: TanStack Query needs the rejection so the dashboard can
  // show its error states, same as the commercial service queue reads.
  public async BatchGetMembershipDashboard(
    req: MembershipDashboard
  ): Promise<MembershipDashboardList> {
    return await this.svc.batchGetMembershipDashboard(req, this.getMetaDataWithoutCache())
      .response;
  }

  public async BatchGetMembershipDashboardRow(
    req: MembershipDashboardRow
  ): Promise<MembershipDashboardRowList> {
    return await this.svc.batchGetMembershipDashboardRow(req, this.getMetaDataWithoutCache())
      .response;
  }
}

export default MembershipClient;
